import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { Button, Form } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import { useMutation, useQuery } from "react-query"
import PaymentAccount, { PaymentAccountDto } from "../../payment-account/model/paymentaccount"
import Session from "../../payment-account/model/session"
import SessionService from "../../payment-account/service/sessionService"
import { useGetPaymentAccounts } from "../../payment-processing/usePaymentAccountSession"
import { CHECKOUT_COOKIE_KEY, PRICING_TABLE_COOKIE_KEY, clearCookies } from "../../price-list/helpers/utils"
import { APIError, HttpError, isError } from "../../state/helpers/Error"
import IntlProps from "../../state/helpers/IntlProps"
import { PAYMENT_SOURCE, PAYMENT_STATUS, PAYMENT_TYPE } from "../../state/helpers/Payment"
import { INTEGRATION_KEY } from "../../state/integration/Constants"
import LoggerService from "../../state/logger/LoggerService"
import PaymentIntentService from "../../state/payment-intent/PaymentIntentService"
import { Metadata } from "../../state/payment-processor/metadata"
import PaymentProcessorService from "../../state/payment-processor/paymentProcessorService"
import { PayinConfig } from "../../state/payment-processor/rainforest/Payin"
import Payment from "../../state/payment/Payment"
import { OrderFormPayload } from "../../state/price-list/orderFormPayload"
import Proposal, { ProposalDto } from "../../state/proposal/Proposal"
import ProposalService from "../../state/proposal/proposalService"
import { SESSION_TYPE, VALID_DOCUMENT_TYPE } from "../../utils"
import { CURRENCY } from "../../utils/currency/constants"
import CircleNumberBadge from "../badge/circle-badge/CircleNumberBadge"
import { WarningIcon } from "../icon/icons/WarningIcon"
import DebouncedInput from "../inputs/DebouncedInput"
import ProcessingPaymentLoader from "./rf-components/ProcessingPaymentLoader"
import RainforestDisabled from "./rf-components/RainforestDisabled"
import RainforestErrorRefreshButton from "./rf-components/RainforestErrorRefreshButton"
import RainforestLoading from "./rf-components/RainforestLoading"
import RainforestPayment, { RFPayInEvent } from "./rf-components/RainforestPayment"
import { RF_COLOR_CODES, RF_METHOD_TYPES } from "./rf-components/constants"
import { messages } from "./rf-components/messages"
import "./rf-components/styles/rainforest-credit-card.scss"

type OwnProps = {
    amount: number
    buildMetaData?: () => Metadata
    onClose: () => void
    proposalHash: string
    paymentAccount?: PaymentAccountDto
    forceSaveCard?: boolean
    buttonColor?: string,
}

type PaymentResult = {
    isNewPaymentCreated: boolean
    status: string
    declinedReason: string
    paymentTransactionStatus: string
}

const REFRESH_INTERVAL = 1000
const MAX_POLL_ATTEMPTS = 30

const RainforestCreditCardWrapper = (props: OwnProps & IntlProps) => {

    const isTemplate = window.location.pathname.startsWith("/templates/")

    const metaData = props.buildMetaData?.()

    const isSupportedCurrency = (): boolean => {
        if (metaData?.accountStatementId) {
            return metaData?.currency == CURRENCY.USD && metaData?.accountStatementInvoices.every(i => i.currency === CURRENCY.USD)
        }
        return metaData?.currency == CURRENCY.USD && metaData?.documentCurrency == CURRENCY.USD
    }

    const isProposal = (): boolean => {
        if (props.buildMetaData) {
            const metaData = props.buildMetaData()
            return metaData.source === PAYMENT_SOURCE.ONLINE
        }
        return false
    }

    let merchantID
    if (!isProposal() || isTemplate) {
        const { paymentAccounts } = useGetPaymentAccounts()
        if (props.buildMetaData) {
            const metaData = props.buildMetaData()
            const pa = paymentAccounts?.find(pa => (pa.getLocationIds().includes(metaData?.locationId)))
            merchantID = pa?.getMerchantId()
        }
    }

    if (props.paymentAccount) {
        const paymentAcc = new PaymentAccount(props.paymentAccount)
        merchantID = paymentAcc.getMerchantId()
    }

    const [isPolling, setIsPolling] = useState<boolean>(false)
    const [paymentFailed, setPaymentFailed] = useState<boolean>(false)
    const [declinedReason, setDeclinedReason] = useState<string>("")
    const [previousPayment, setPreviousPayment] = useState<Payment | undefined>(undefined)
    const [saveCardOnfile, setSaveCardOnFile] = useState<boolean>(true)
    const [cardholderName, setCardholderName] = useState<string>("")
    const [isTyping, setIsTyping] = useState<boolean>(false)
    const [isRFLoaded, setisRFLoaded] = useState<boolean>(false)
    const [tentativeError, setTentativeError] = useState<string>("")
    const [isFaded, setIsFaded] = useState<boolean>(false)
    const [isThreedsModalOpened, setIsThreedsModalOpened] = useState<boolean>(false)
    //should ONLY be set for order forms
    const [finalProposalHash, setFinalProposalHash] = useState<string>("")
    const [temporaryFinalProposalHash, setTemporaryFinalProposalHash] = useState<string>("")

    const createPayinConfig = async () => {
        if (props.buildMetaData) {
            const metaData = props.buildMetaData()
            metaData.savePaymentMethodOnFile = saveCardOnfile
            metaData.paymentMethod = PAYMENT_TYPE.CREDIT_CARD
            metaData.ignoreWebhook = isPayingOrderForm()
            setisRFLoaded(false)
            return await PaymentProcessorService.createPayinConfig(
                props.amount,
                merchantID,
                cardholderName || "",
                metaData,
                INTEGRATION_KEY.RAINFOREST,
            )
        }
    }

    const isPayingOrderForm = (): boolean => {
        return metaData?.documentType == VALID_DOCUMENT_TYPE.PRICE_LIST
    }

    const isPayingAccountStatementProposal = (): boolean => {
        return isProposal() && !!metaData?.accountStatementId
    }

    const isAmountValid = (): boolean => {
        return !!props.amount && props.amount > 0 && !isNaN(props.amount)
    }

    const isDataValidForQuery = (): boolean => {
        return !!merchantID && isAmountValid() && !!props.buildMetaData && cardholderName !== ""
    }

    const { data: payinConfigData, isLoading: isLoadingPayingConfig, isFetching: isFetchingPayingConfig, refetch } = useQuery(
        ["createPayinConfig", merchantID, props.amount, cardholderName],
        createPayinConfig,
        {
            enabled: isDataValidForQuery()
        }
    )

    useEffect(() => {
        const component = document.querySelector("rainforest-payment")
        if (component) {
            const delay = setTimeout(() => {
                setisRFLoaded(true)
                clearTimeout(delay)
            }, 1000)
        }
        return () => {
            if (component) {
                component.removeEventListener("approved", (event) => handleApprovedEvent(event, {}))
            }
        }
    })

    useEffect(() => {
        if (isPayingOrderForm() && finalProposalHash && !isThreedsModalOpened) {
            pollForPaymentCreation()
        } else if (!props.proposalHash && metaData?.transactions && finalProposalHash && !isThreedsModalOpened) {
            pollForPaymentCreation()
        }
    }, [finalProposalHash])

    useEffect(() => {
        if (props.buildMetaData && payinConfigData && !isError(payinConfigData)) {
            PaymentIntentService.createPaymentIntent({
                externalId: payinConfigData.getId(),
                paymentProcessorKey: INTEGRATION_KEY.RAINFOREST,
                paymentMethodType: PAYMENT_TYPE.CREDIT_CARD,
                savePaymentMethodOnFile: saveCardOnfile,
                accountId: props.buildMetaData().accountId,
            })
        }
    }, [payinConfigData, saveCardOnfile])


    useEffect(() => {
        const fetchData = async () => {
            const proposal = await fetchProposal(props.proposalHash)
            if (proposal) {
                setPreviousPayment(proposal.getMostRecentPayment())
            }
        }
        if (props.proposalHash && !isTemplate) {
            fetchData()
        }
    }, [])

    const { data: session, isLoading: isLoadingSession } = useQuery(
        ["createSession"],
        () => SessionService.getSession(SESSION_TYPE.CREATE_PAYIN, merchantID, isProposal()),
        {
            enabled: !!merchantID
        }
    )

    const { mutateAsync: createPaymentTransaction, isLoading: isCreatingTransaction } = useMutation(
        ({ hash, payload }: { hash: string, payload: OrderFormPayload }) => {
            return ProposalService.createPaymentTransaction(hash, payload)
        })

    const pollForPaymentCreation = async () => {
        setIsPolling(true)

        let pollAttempt = 0
        const poll = setInterval(async () => {
            if (pollAttempt >= MAX_POLL_ATTEMPTS) {
                clearInterval(poll)
                setIsPolling(false)
                return
            }

            const { isNewPaymentCreated, status, declinedReason, paymentTransactionStatus } = await refreshDocument()

            //TODO: MAKE BETTER PLS
            if (status != PAYMENT_STATUS.PENDING && isNewPaymentCreated && paymentTransactionStatus !== PAYMENT_STATUS.PENDING) {
                clearInterval(poll)
                setIsPolling(false)
                if (status == PAYMENT_STATUS.DECLINED) {
                    setPaymentFailed(true)
                    setDeclinedReason(declinedReason)
                } else {
                    if (isPayingOrderForm()) {
                        setIsFaded(true)
                        setIsTyping(true)
                        clearCookies(PRICING_TABLE_COOKIE_KEY)
                        clearCookies(CHECKOUT_COOKIE_KEY)
                        window.location.href = "/p/proposals/" + finalProposalHash
                    } else {
                        props.onClose()
                    }
                }
                if (!isPayingOrderForm()) {
                    setIsFaded(false)
                    setIsTyping(false)
                }
            }

            pollAttempt++
        }, REFRESH_INTERVAL)
    }

    const fetchProposal = async (hash: string): Promise<Proposal | undefined> => {
        const response = await ProposalService.getProposal(hash)
        return isError(response) ? undefined : response as Proposal
    }

    const refreshDocument = async (): Promise<PaymentResult> => {
        const proposal = await fetchProposal(finalProposalHash || props.proposalHash)
        if (proposal) {
            const newlyCreatedPayment = proposal.getMostRecentPayment()
            //handle case when no payments exist on document yet
            if (!newlyCreatedPayment) {
                return {
                    isNewPaymentCreated: false,
                    status: "",
                    declinedReason: "",
                    paymentTransactionStatus: "",
                }
            }

            const isNewPaymentCreated = validateNewPaymentWasCreated(newlyCreatedPayment)
            const paymentTransaction = proposal.getPaymentTransactionByPaymentId(newlyCreatedPayment.getId())
            if (isNewPaymentCreated && paymentTransaction) {
                setPreviousPayment(newlyCreatedPayment)
            }

            return {
                isNewPaymentCreated,
                status: newlyCreatedPayment.getStatus(),
                declinedReason: newlyCreatedPayment.getDeclinedReason(),
                paymentTransactionStatus: paymentTransaction?.getStatus() || ""
            }
        }

        return {
            isNewPaymentCreated: false,
            status: "",
            declinedReason: "",
            paymentTransactionStatus: "",
        }
    }


    const validateNewPaymentWasCreated = (newlyCreatedPayment: Payment): boolean => {
        if (newlyCreatedPayment && !previousPayment) {
            return true
        }

        return previousPayment?.getId() !== newlyCreatedPayment?.getId()
    }

    const handleErrorEvent = (event: RFPayInEvent) => {
        const refusalDesc = event.detail?.[0]?.data?.refusal_desc ?? ""
        setDeclinedReason(refusalDesc)

        setPaymentFailed(true)
    }

    const handleClickEvent = () => {
        if (isError(payinConfigData)) {
            setIsTyping(false)
            setIsFaded(false)
            return
        }

        if (isPayingOrderForm()) {
            setFinalProposalHash(temporaryFinalProposalHash)
            return
        }
        //can't poll when paying multiple invoices with one payment
        if (props.proposalHash) {
            pollForPaymentCreation()
        }
    }

    const isPayinConfig = (payinConfig: PayinConfig | HttpError | undefined)
        : payinConfig is PayinConfig => {
        return payinConfig != undefined && !isError(payinConfig)
    }

    const handleButtonClick = () => {
        setPaymentFailed(false)
        setCardholderName("")
        setisRFLoaded(false)
        setIsFaded(false)
        setIsTyping(false)
        if (isDataValidForQuery()) {
            refetch()
        }
    }


    const documentTypeAttribute = (documentType: string) => {
        switch (documentType) {
        case "invoice":
            return "invoiceId"
        case "quote":
            return "quoteId"
        case "order":
            return "orderId"
        default:
            return ""
        }
    }

    const handleApprovedEvent = (event, payload: object) => {
        {/* @ts-ignore */ }
        const rfResponse = event?.detail[0]?.data
        LoggerService.LogRequest({
            logMessage: "Payment Is Approved Event - Credit Card Payment",
            logBody: {
                payinConfigId: rfResponse.payin_config_id,
                amount: props.amount,
                cardholderName: cardholderName,
                payinId: rfResponse.payin_id,
                payload: payload,
            }
        })
    }

    const generateLogPayload = (logMessage, requestPayload, response) => {
        const payinConfigId = isPayinConfig(payinConfigData) ? payinConfigData.getId() : ""
        const identifiers = response?.data?.map(doc => doc.attributes.identifier).join(", ")
        const documentIds = response?.data?.map(doc => doc.id).join(", ")
        const errorMessage = response?.error?.message

        return {
            logMessage: logMessage,
            logBody: {
                payinConfigId: payinConfigId,
                amount: props.amount,
                cardholderName: cardholderName,
                paymentMethod: PAYMENT_TYPE.CREDIT_CARD,
                proposalHash: props.proposalHash,
                payload: requestPayload,
                identifiers: identifiers,
                documentIds: documentIds,
                errorMessage: errorMessage
            }
        }
    }

    const tentativePaymentCreation = async () => {
        //Paying on proposal
        if (metaData && isPayinConfig(payinConfigData) && isProposal()) {
            setIsFaded(true)
            setIsTyping(true)
            const attributeId = documentTypeAttribute(metaData.documentType)
            let payload = {
                note: "Online payment",
                paymentType: PAYMENT_TYPE.CREDIT_CARD,
                currency: "USD",
                contactId: metaData.contactId,
                source: metaData.source,
                transactions: [
                    {
                        amount: metaData.amount,
                        [attributeId]: metaData.documentId
                    }
                ],
                fingerprintId: metaData.fingerprintId,
                externalId: payinConfigData.getId(),
                creditCard: {
                    locationId: metaData.locationId
                }
            } as OrderFormPayload

            if (isPayingOrderForm()) {
                payload = metaData.orderFormPayload
                payload.externalId = payinConfigData.getId()
                payload.creditCard = {
                    locationId: metaData.locationId
                }
            } else if (isPayingAccountStatementProposal()) {
                const transactions = metaData.accountStatementInvoices.map(inv => {
                    return {
                        invoiceId: inv.id,
                        amount: inv.balance,
                    }
                }).filter(trans => parseFloat(trans.amount) > 0)
                payload.transactions = transactions
            }

            LoggerService.LogRequest(generateLogPayload(
                "Payment transaction request before",
                payload,
                {},
            ))
            const res = await createPaymentTransaction({ hash: props.proposalHash, payload: payload })

            LoggerService.LogRequest(generateLogPayload(
                "Payment transaction request after",
                payload,
                res,
            ))

            if ((res as ProposalDto).id) {
                const component = document.querySelector("rainforest-payment")
                if (component) {
                    component.addEventListener("approved", (event) => handleApprovedEvent(event, payload))

                    LoggerService.LogRequest(generateLogPayload(
                        "Rainforest credit card component submitting - online proposal",
                        payload,
                        res,
                    ))

                    {/* @ts-ignore */ }
                    component.submit()
                    if (isPayingOrderForm()) {
                        setTemporaryFinalProposalHash((res as ProposalDto).attributes.hash)
                    }
                }
            } else {
                if ((res as APIError).error?.message) {
                    const errorMessage = (res as APIError).error?.message
                    setTentativeError(errorMessage || "")
                } else {
                    setTentativeError(props.intl.formatMessage(messages.genericErrorMessage))
                }
            }

            //paying from back-office
        } else {
            if (metaData && isPayinConfig(payinConfigData)) {
                setIsFaded(true)
                setIsTyping(true)
                const attributeId = documentTypeAttribute(metaData.documentType)
                const payload = {
                    "note": metaData.notes,
                    "paymentType": PAYMENT_TYPE.CREDIT_CARD,
                    "currency": "USD",
                    "contactId": metaData.contactId,
                    "source": metaData.source,
                    transactions: [
                        {
                            "amount": metaData.amount,
                            [attributeId]: metaData.documentId
                        }
                    ],
                    "fingerprintId": metaData.fingerprintId,
                    "externalId": payinConfigData.getId(),
                    "creditCard": {
                        locationId: metaData.locationId
                    }
                }

                if (metaData?.transactions) {
                    payload.transactions = metaData.transactions
                }

                LoggerService.LogRequest(generateLogPayload(
                    "Payment transaction request before",
                    payload,
                    {},
                ))

                AppContext.getApiClient()?.post("/payment-transactions", payload).then(res => {

                    LoggerService.LogRequest(generateLogPayload(
                        "Payment transaction request after",
                        payload,
                        res,
                    ))

                    if (res.data) {
                        //when paying multiple invoices from contact page
                        if (!props.proposalHash) {
                            {/* @ts-ignore */ }
                            const response = res
                            if (response.data.length > 0) {
                                const lastIndex = response.data.length - 1
                                setFinalProposalHash(response.data[lastIndex].attributes.proposalHash)
                            }
                        }
                        const component = document.querySelector("rainforest-payment")
                        if (component) {
                            component.addEventListener("approved", (event) => handleApprovedEvent(event, payload))

                            LoggerService.LogRequest(generateLogPayload(
                                "Rainforest credit card component submitting - back-office",
                                payload,
                                res,
                            ))

                            {/* @ts-ignore */ }
                            component.submit()
                        }
                    } else {
                        if ((res as APIError).error?.message) {
                            const errorMessage = (res as APIError).error?.message
                            setTentativeError(errorMessage || "")
                        } else {
                            setTentativeError(props.intl.formatMessage(messages.genericErrorMessage))
                        }
                    }
                })
            }
        }
    }


    if (!isAmountValid()) {
        return <div>
            <p className="text-danger">
                <FormattedMessage
                    id="common.credit_card.rainforest.error_msg"
                    defaultMessage="Please enter a valid payment amount. The amount should be a positive number greater than zero."
                />
            </p>
        </div>
    }

    if (paymentFailed) {
        return (
            <div>
                <strong className="mr-auto">
                    <FormattedMessage
                        id="common.credit_card.rainforest.error_header"
                        defaultMessage="Error"
                    /></strong>
                <p className="text-danger">
                    <FormattedMessage
                        tagName="div"
                        id="common.credit_card.rainforest.error_message_with_decline_reason"
                        defaultMessage={
                            "Uh oh. The following error occured during payment processing: {declinedReason} "
                            + "Make sure everything is correct, and try again."
                        }
                        values={{ declinedReason }}
                    />
                </p>
                <Button onClick={handleButtonClick}>
                    <FormattedMessage
                        tagName="div"
                        id="common.credit_card.rainforest.try_again_btn"
                        defaultMessage="Try Again"
                    />
                </Button>
            </div>
        )
    }

    if (!isSupportedCurrency()) {
        return (
            <div className="py-4 px-2 d-flex gap-2">
                <WarningIcon className="text-warning" size={32} />
                <FormattedMessage
                    id="common.credit_card.rainforest_credit_card_wrapper.unsupported_currency"
                    defaultMessage="To process payments via QuoteMachine Payments, the account and document currency must be set to USD."
                />
            </div>
        )
    }


    const getBgColor = (isCircleBadge: boolean): string => {
        if (isProposal() && props.buttonColor) {
            return props.buttonColor
        }
        if (isCircleBadge) {
            return RF_COLOR_CODES.BLUE
        }
        return RF_COLOR_CODES.PAY_BTN_BLUE
    }

    const refreshProposal = () => {
        if (isProposal()) {
            window.location.href = "/p/proposals/" + props.proposalHash
        } else {
            window.location.reload()
        }
    }

    const circleBadgeColor = getBgColor(true)
    const buttonColor = getBgColor(false)

    const isLoading = isTyping || isLoadingPayingConfig || isFetchingPayingConfig || isLoadingSession || isCreatingTransaction
    const displayLoading = ((!isRFLoaded && cardholderName !== "") || isLoading) && !isThreedsModalOpened
    const displayDisabled = (!isLoading && cardholderName === "") && !isThreedsModalOpened
    const displayCreditCard = isPayinConfig(payinConfigData) && (!isLoading || isFaded || isThreedsModalOpened) 

    return (
        isPolling ?
            <ProcessingPaymentLoader currencyCode={metaData?.currency || CURRENCY.USD} />
            :
            <div className={isCreatingTransaction ? "dimmed" : ""}>
                {!tentativeError && <div className="container-rf">
                    <div className="flex-wrap d-flex header-row">
                        <CircleNumberBadge bgColor={circleBadgeColor} fontColor={RF_COLOR_CODES.WHITE} number="1" />
                        <Form.Label className="ml-2 mt-1 mb-3">
                            {props.intl.formatMessage(messages.cardholderName)}
                        </Form.Label>
                    </div>
                    <DebouncedInput
                        onInputChange={(name: string) => setCardholderName(name)}
                        onTypingStatusChange={(isTyping: boolean) => setIsTyping(isTyping)}
                        placeholder={props.intl.formatMessage(messages.cardholderName)}
                        className="cardholder-input"
                    />
                    <div className="flex-wrap d-flex header-row mt-3">
                        <CircleNumberBadge
                            bgColor={cardholderName !== "" ? circleBadgeColor : RF_COLOR_CODES.GREY}
                            fontColor={RF_COLOR_CODES.WHITE}
                            number="2" />
                        <Form.Label className="ml-2 mt-1">
                            {props.intl.formatMessage(messages.cardInformation)}
                        </Form.Label>
                    </div>
                    <div className={displayDisabled ? "fade-in-pt30" : "fade-out"}>
                        <RainforestDisabled
                            payBtnColor={buttonColor}
                            method={RF_METHOD_TYPES.PAY_CC}
                            amount={props.amount}
                            currency={props.buildMetaData?.().currency}
                        />
                    </div>
                    <div className={displayLoading && !tentativeError ? "fade-in-pt30" : "fade-out"}>
                        <RainforestLoading payBtnColor={buttonColor} method={RF_METHOD_TYPES.PAY_CC} />
                    </div>
                </div >}

                {displayCreditCard && !tentativeError &&
                    <div className={((!isRFLoaded || isFaded) && !isThreedsModalOpened) ? "fade-out" : "fade-in"}>
                        <RainforestPayment
                            sessionKey={(session as Session).getSessionKey()}
                            payinConfigID={payinConfigData.getId()}
                            allowedMethods='CARD'
                            onSubmit={handleClickEvent}
                            threedsAttemptOpened={() => {
                                setIsThreedsModalOpened(true)
                            }}
                            threedsAttemptClosed={() => {
                                setIsThreedsModalOpened(false)
                            }}
                            handleErrorEvent={handleErrorEvent}
                            buttonColor={props.buttonColor}
                            method={RF_METHOD_TYPES.PAY_CC}
                            amount={props.amount}
                            currency={props.buildMetaData?.().currency}
                            refresh={tentativePaymentCreation}
                            tentativeError={tentativeError}
                        />
                        {!props.forceSaveCard &&
                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="ml-3 custom-control custom-checkbox">
                                        <input
                                            style={{ zIndex: 10 }}
                                            name="saveCardOnFile"
                                            type="checkbox"
                                            id="saveCardOnFile"
                                            className="custom-control-input"
                                            checked={saveCardOnfile}
                                            onChange={() => setSaveCardOnFile(!saveCardOnfile)}
                                        />
                                        <label htmlFor="saveCardOnFile" className="custom-control-label" style={{ fontSize: "16px" }}>
                                            {props.intl.formatMessage(messages.saveCardOnFile)}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                }
                {tentativeError &&
                    <RainforestErrorRefreshButton
                        errorMessage={tentativeError}
                        onClick={refreshProposal}
                    />
                }
            </div>
    )
}
export default injectIntl(RainforestCreditCardWrapper)


// WEBPACK FOOTER //
// ./src/common/credit-card/RainforestCreditCardWrapper.tsx